// positions-accordion.tsx

import {
    Accordion,
    AccordionSummary,
    Box,
    Button,
    Grid,
    IconButton,
    TextField,
    Typography,
    useTheme,
    Collapse,
    Chip,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import DeleteIcon from "icons/DeleteIcon";
import AddIcon from "@mui/icons-material/Add";
import { v4 as uuidv4 } from 'uuid';
import { H3 } from "components/Typography";
import { LocalizationProvider, TimePicker } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { it } from "date-fns/locale";
import { useTranslation } from "react-i18next";
import { FC, useEffect, useState } from "react";
import { useSeason } from "contexts/SeasonContext";
import { workPositionsApi } from "api";
import { ShiftDetailLocal } from "page-sections/data-table/dataTableV2/AddShiftModal";
import { ShiftEntity } from "api/generated";
import { ignoreUTC } from "utils/utils";

interface WorkPositionEntity {
    id: number;
    name: string;
}

interface PositionAccordionProps {
    data: ShiftDetailLocal[];
    setData: (details: ShiftDetailLocal[]) => void;
    openAddShiftDetail: () => void;
    turnStart: Date;
    turnEnd: Date;
    departmentId: number;
    assignments: ShiftEntity[];
    shiftNotes?: string | null;
    onShiftNotesChange?: (notes: string | null) => void;
}

/**
 * We remove "timeZone" from timeOption to avoid local-time shifts. 
 * If you want a specific zone, you can add: { timeZone: "UTC" } or similar.
 */
const timeOption: Intl.DateTimeFormatOptions = {
    hour: "2-digit",
    minute: "2-digit",
    timeZone: "UTC"
};

const PositionAccordion: FC<PositionAccordionProps> = ({
    data,
    setData,
    openAddShiftDetail,
    turnStart,
    turnEnd,
    departmentId,
    assignments,
    shiftNotes,
    onShiftNotesChange
}) => {
    const theme = useTheme();
    const { t } = useTranslation();
    const { seasonId } = useSeason();

    const [workPositions, setWorkPositions] = useState<WorkPositionEntity[]>([]);
    const [editableMap, setEditableMap] = useState<Map<number | string, boolean>>(new Map());
    const [notes, setNotes] = useState<string | null>(shiftNotes || null);
    const [isNotesEditable, setIsNotesEditable] = useState<boolean>(false);
    const [expandedAdditionalBreaks, setExpandedAdditionalBreaks] = useState<Map<number | string, boolean>>(new Map());

    // Shared additional break (applied to every resource at once)
    const [sharedBreakStart, setSharedBreakStart] = useState<Date | null>(null);
    const [sharedBreakEnd, setSharedBreakEnd] = useState<Date | null>(null);

    // Update notes when shiftNotes prop changes
    useEffect(() => {
        setNotes(shiftNotes || null);
    }, [shiftNotes]);

    // Fetch relevant positions once
    useEffect(() => {
        const fetchPositions = async () => {
            try {
                const positionIds = Array.from(new Set(data.map((d) => d.workPositionId)));
                const allWps = (await workPositionsApi.findAll(departmentId, seasonId)).data;
                setWorkPositions(allWps.filter((wp) => positionIds.includes(wp.id)));
            } catch (err) {
                console.error("Failed to load work positions", err);
            }
        };
        fetchPositions();
    }, [data, departmentId, seasonId]);

    // Ensure we have an editableMap entry for each detail
    useEffect(() => {
        const newMap = new Map(editableMap);
        const newExpandedMap = new Map(expandedAdditionalBreaks);
        data.forEach((detail) => {
            const id = detail.id !== 0 ? detail.id : detail.localId;
            if (id) {
                if (detail.id !== 0 && !newMap.has(detail.id)) {
                    newMap.set(detail.id, false);
                }
                else if (detail.localId && !newMap.has(detail.localId)) {
                    newMap.set(detail.localId, false);
                }
                // Initialize expanded state to true if not set (show additional breaks by default)
                if (!newExpandedMap.has(id)) {
                    newExpandedMap.set(id, true);
                }
            }
        });
        setEditableMap(newMap);
        setExpandedAdditionalBreaks(newExpandedMap);
    }, [data]);
    const visibleDetails = data.filter((d) => !d._isDeleted);

    const updateDetail = (detailId: number, partial: Partial<ShiftDetailLocal>, localId?: string) => {
        if (detailId === 0 && !localId)
            return;
        setData(data.map((d) => {
            if (detailId === 0) {
                return (d.localId === localId ? { ...d, ...partial } : d)
            }
            return (d.id === detailId ? { ...d, ...partial } : d)
        }));
    };

    const markDetailDeleted = (detailId: number, localId?: string) => {
        if (detailId === 0 && !localId)
            return;
        setData(data.map((d) => {
            if (detailId === 0) {
                return (d.localId === localId ? { ...d, _isDeleted: true } : d)
            }
            return (d.id === detailId ? { ...d, _isDeleted: true } : d)
        }));
    };

    const handleNotesChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const newNotes = event.target.value || null;
        setNotes(newNotes);
        if (onShiftNotesChange) {
            onShiftNotesChange(newNotes);
        }
    };

    const toggleNotesEdit = () => {
        setIsNotesEditable(!isNotesEditable);
    };

    // Helper function to get shift time range for a detail
    const getShiftTimeRange = (item: ShiftDetailLocal) => {
        const start = item.startTime ? new Date(item.startTime) : turnStart;
        const end = item.endTime ? new Date(item.endTime) : turnEnd;
        return { start, end };
    };

    // Helper: Extract time in milliseconds from ISO string directly
    // This avoids timezone conversion issues by parsing the ISO string directly
    const getTimeFromISOString = (isoString: string): number => {
        // ISO string format: "1970-01-01T15:00:00.000Z" or "2024-01-01T15:00:00.000Z"
        // Extract time portion (HH:MM:SS) and convert to milliseconds
        const match = isoString.match(/T(\d{2}):(\d{2}):(\d{2})/);
        if (match) {
            const hours = parseInt(match[1], 10);
            const minutes = parseInt(match[2], 10);
            const seconds = parseInt(match[3], 10);
            return hours * 3600000 + minutes * 60000 + seconds * 1000;
        }
        // Fallback: parse as date and extract UTC time
        const date = new Date(isoString);
        return date.getUTCHours() * 3600000 + date.getUTCMinutes() * 60000 + date.getUTCSeconds() * 1000;
    };

    // Helper: Extract time from Date object (for dates that have been through ignoreUTC)
    const getTimeFromDate = (date: Date): number => {
        // When date has been through ignoreUTC, we need to extract the time component
        // The date might have timezone adjustments, so we use UTC methods
        return date.getUTCHours() * 3600000 + date.getUTCMinutes() * 60000 + date.getUTCSeconds() * 1000;
    };

    // Validation: Check if break times are within shift time range
    // IMPORTANT: We need to pass the ISO strings, not the Date objects, to avoid timezone issues
    const isBreakTimeValid = (breakStartISO: string, breakEndISO: string, item: ShiftDetailLocal): boolean => {
        const breakStartTime = getTimeFromISOString(breakStartISO);
        const breakEndTime = getTimeFromISOString(breakEndISO);
        const shiftStartTime = item.startTime ? getTimeFromISOString(item.startTime) : (() => {
            const { start } = getShiftTimeRange(item);
            return getTimeFromDate(start);
        })();
        const shiftEndTime = item.endTime ? getTimeFromISOString(item.endTime) : (() => {
            const { end } = getShiftTimeRange(item);
            return getTimeFromDate(end);
        })();
        return breakStartTime >= shiftStartTime && breakEndTime <= shiftEndTime && breakStartTime < breakEndTime;
    };

    // Validation: Check if break overlaps with main break
    // IMPORTANT: We need to pass the ISO strings, not the Date objects, to avoid timezone issues
    const doesBreakOverlapWithMain = (breakStartISO: string, breakEndISO: string, item: ShiftDetailLocal): boolean => {
        if (!item.breakStart || !item.breakEnd || !breakStartISO || !breakEndISO) return false;
        
        // Extract times directly from ISO strings to avoid timezone issues
        const breakStartTime = getTimeFromISOString(breakStartISO);
        const breakEndTime = getTimeFromISOString(breakEndISO);
        const mainStartTime = getTimeFromISOString(item.breakStart);
        const mainEndTime = getTimeFromISOString(item.breakEnd);
        
        // Overlap: Two breaks overlap if one starts before the other ends AND ends after the other starts
        // They DON'T overlap if: breakStart >= mainEnd OR breakEnd <= mainStart
        // Example: 15:00-16:00 (main) and 16:15-16:30 (additional) do NOT overlap
        //   because 16:15 >= 16:00 (breakStart >= mainEnd)
        // Example: 15:00-16:00 (main) and 15:30-16:30 (additional) DO overlap
        //   because 15:30 < 16:00 AND 16:30 > 15:00
        return breakStartTime < mainEndTime && breakEndTime > mainStartTime;
    };

    // Validation: Check if break overlaps with other additional breaks
    // IMPORTANT: We need to pass the ISO strings, not the Date objects, to avoid timezone issues
    const doesBreakOverlapWithOthers = (breakStartISO: string, breakEndISO: string, item: ShiftDetailLocal, excludeBreakId?: string): boolean => {
        if (!item.additionalBreaks || item.additionalBreaks.length === 0 || !breakStartISO || !breakEndISO) return false;
        const breakStartTime = getTimeFromISOString(breakStartISO);
        const breakEndTime = getTimeFromISOString(breakEndISO);
        return item.additionalBreaks.some(ab => {
            // Skip the break we're currently editing (check both localBreakId and id)
            if (excludeBreakId && (ab.localBreakId === excludeBreakId || (ab.id && ab.id.toString() === excludeBreakId))) return false;
            // Skip breaks without valid times
            if (!ab.startTime || !ab.endTime || ab.startTime.trim() === '' || ab.endTime.trim() === '') return false;
            const abStartTime = getTimeFromISOString(ab.startTime);
            const abEndTime = getTimeFromISOString(ab.endTime);
            // Overlap: Two breaks overlap if one starts before the other ends AND ends after the other starts
            // They DON'T overlap if: breakStart >= abEnd OR breakEnd <= abStart
            return breakStartTime < abEndTime && breakEndTime > abStartTime;
        });
    };

    // Add additional break
    const addAdditionalBreak = (detailId: number, localId?: string) => {
        if (detailId === 0 && !localId) return;
        setData(data.map((d) => {
            if (detailId === 0) {
                if (d.localId === localId) {
                    const newBreak = {
                        startTime: '',
                        endTime: '',
                        localBreakId: uuidv4(),
                    };
                    return {
                        ...d,
                        additionalBreaks: [...(d.additionalBreaks || []), newBreak]
                    };
                }
                return d;
            }
            if (d.id === detailId) {
                const newBreak = {
                    startTime: '',
                    endTime: '',
                    localBreakId: uuidv4(),
                };
                return {
                    ...d,
                    additionalBreaks: [...(d.additionalBreaks || []), newBreak]
                };
            }
            return d;
        }));
    };

    // Apply one additional break (same times) to every visible resource at once.
    // Useful when a break is shared by all resources and shouldn't be reconfigured per resource.
    const applySharedBreakToAll = () => {
        if (!sharedBreakStart || !sharedBreakEnd) return;

        const normalize = (date: Date): string => {
            const value = new Date(date);
            value.setSeconds(0, 0);
            value.setFullYear(1970, 0, 1);
            return ignoreUTC(value).toISOString();
        };
        const startTime = normalize(sharedBreakStart);
        const endTime = normalize(sharedBreakEnd);

        setData(data.map((d) => {
            if (d._isDeleted) return d;
            return {
                ...d,
                additionalBreaks: [
                    ...(d.additionalBreaks || []),
                    { startTime, endTime, localBreakId: uuidv4() },
                ],
            };
        }));

        // Reset the shared inputs after applying
        setSharedBreakStart(null);
        setSharedBreakEnd(null);
    };

    // Remove additional break
    const removeAdditionalBreak = (detailId: number, localId: string | undefined, breakIdentifier: string) => {
        if (detailId === 0 && !localId) return;
        setData(data.map((d) => {
            if (detailId === 0) {
                if (d.localId === localId) {
                    return {
                        ...d,
                        additionalBreaks: (d.additionalBreaks || []).filter(ab => 
                            ab.localBreakId !== breakIdentifier && (ab.id?.toString() !== breakIdentifier)
                        )
                    };
                }
                return d;
            }
            if (d.id === detailId) {
                return {
                    ...d,
                    additionalBreaks: (d.additionalBreaks || []).filter(ab => 
                        ab.localBreakId !== breakIdentifier && (ab.id?.toString() !== breakIdentifier)
                    )
                };
            }
            return d;
        }));
    };

    // Update additional break
    const updateAdditionalBreak = (detailId: number, localId: string | undefined, breakIdentifier: string, partial: Partial<{ startTime: string; endTime: string }>) => {
        if (detailId === 0 && !localId) return;
        setData(data.map((d) => {
            if (detailId === 0) {
                if (d.localId === localId) {
                    return {
                        ...d,
                        additionalBreaks: (d.additionalBreaks || []).map(ab =>
                            // Match by localBreakId or by id (if breakIdentifier is a number string)
                            (ab.localBreakId === breakIdentifier || (ab.id && ab.id.toString() === breakIdentifier)) ? { ...ab, ...partial } : ab
                        )
                    };
                }
                return d;
            }
            if (d.id === detailId) {
                return {
                    ...d,
                    additionalBreaks: (d.additionalBreaks || []).map(ab =>
                        // Match by localBreakId or by id (if breakIdentifier is a number string)
                        (ab.localBreakId === breakIdentifier || (ab.id && ab.id.toString() === breakIdentifier)) ? { ...ab, ...partial } : ab
                    )
                };
            }
            return d;
        }));
    };

    // Toggle additional breaks section
    const toggleAdditionalBreaks = (id: number | string) => {
        const newMap = new Map(expandedAdditionalBreaks);
        newMap.set(id, !newMap.get(id));
        setExpandedAdditionalBreaks(newMap);
    };

    return (
        <Box maxHeight={"50vh"} overflow={"auto"} paddingTop={2}>
            {/* Shift Notes Section */}
            <Box 
                sx={{ 
                    mb: 3, 
                    p: 2, 
                    borderRadius: 2, 
                    backgroundColor: 'rgba(33, 150, 243, 0.05)',
                    border: '1px solid',
                    borderColor: 'rgba(33, 150, 243, 0.2)'
                }}
            >
                <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} sm={3}>
                        <Typography 
                            variant="subtitle1" 
                            fontWeight="bold"
                            sx={{ 
                                color: theme.palette.primary.main,
                                display: 'flex',
                                alignItems: 'center'
                            }}
                        >
                            {t("shift.notes")}
                        </Typography>
                    </Grid>
                    <Grid item xs={12} sm={7}>
                        {isNotesEditable ? (
                            <TextField
                                fullWidth
                                multiline
                                rows={2}
                                variant="outlined"
                                size="small"
                                value={notes || ''}
                                onChange={handleNotesChange}
                                placeholder={t("shift.notesPlaceholder")}
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        borderRadius: '8px',
                                        backgroundColor: 'white',
                                    }
                                }}
                            />
                        ) : (
                            <Box
                                sx={{
                                    p: 2,
                                    borderRadius: '8px',
                                    backgroundColor: 'white',
                                    minHeight: '56px',
                                    border: '1px solid',
                                    borderColor: notes ? 'rgba(76, 175, 80, 0.3)' : 'rgba(0, 0, 0, 0.12)',
                                    color: notes ? theme.palette.text.primary : theme.palette.text.secondary,
                                    display: 'flex',
                                    alignItems: 'center'
                                }}
                            >
                                {notes || t("shift.noNotes")}
                            </Box>
                        )}
                    </Grid>
                    <Grid item xs={12} sm={2} sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                        <Button
                            variant="outlined"
                            size="small"
                            onClick={toggleNotesEdit}
                            sx={{ 
                                borderRadius: '8px',
                                minWidth: '100px'
                            }}
                        >
                            {isNotesEditable ? t("account.confirm.button") : t("common.forms.button.edit")}
                        </Button>
                    </Grid>
                </Grid>
            </Box>

            <Grid container paddingBottom={2} paddingTop={1} spacing={1} direction={"row-reverse"}>
                <Grid item xs={3}>
                    <Button
                        fullWidth
                        endIcon={<AddIcon />}
                        variant="contained"
                        onClick={() => { openAddShiftDetail() }}
                    >
                        {t("shift.addResource")}
                    </Button>
                </Grid>
            </Grid>

            {/* Shared additional break: define once and apply to every resource */}
            {visibleDetails.length > 0 && (
                <Box
                    sx={{
                        mb: 3,
                        p: 2,
                        borderRadius: 2,
                        backgroundColor: 'rgba(33, 150, 243, 0.05)',
                        border: '1px solid',
                        borderColor: 'rgba(33, 150, 243, 0.2)',
                    }}
                >
                    <Typography
                        variant="subtitle2"
                        fontWeight="bold"
                        sx={{ color: theme.palette.primary.main, mb: 1 }}
                    >
                        {t("shift.addBreakToAllResources")}
                    </Typography>
                    <Grid container spacing={1} alignItems="center">
                        <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={it}>
                            <Grid item xs={12} sm={4}>
                                <TimePicker
                                    label={t("shift.breakStart")}
                                    value={sharedBreakStart}
                                    onChange={(value) => setSharedBreakStart(value && !isNaN(+value) ? value : null)}
                                    slotProps={{
                                        actionBar: { actions: ["clear"] },
                                        textField: { size: 'small', fullWidth: true },
                                    }}
                                />
                            </Grid>
                            <Grid item xs={12} sm={4}>
                                <TimePicker
                                    label={t("shift.breakEnd")}
                                    value={sharedBreakEnd}
                                    onChange={(value) => setSharedBreakEnd(value && !isNaN(+value) ? value : null)}
                                    slotProps={{
                                        actionBar: { actions: ["clear"] },
                                        textField: { size: 'small', fullWidth: true },
                                    }}
                                />
                            </Grid>
                        </LocalizationProvider>
                        <Grid item xs={12} sm={4}>
                            <Button
                                fullWidth
                                endIcon={<AddIcon />}
                                variant="outlined"
                                disabled={!sharedBreakStart || !sharedBreakEnd}
                                onClick={applySharedBreakToAll}
                            >
                                {t("shift.applyToAllResources")}
                            </Button>
                        </Grid>
                    </Grid>
                </Box>
            )}

            {visibleDetails.length > 0 &&
                workPositions.map((wp) => {
                    const items = visibleDetails.filter((i) => i.workPositionId === wp.id);

                    return (
                        <Accordion key={wp.id}>
                            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                <Grid container alignContent={"center"} justifyContent={"center"} padding={"10px"}>
                                    <Grid item xs={9} sm={11} zeroMinWidth>
                                        <H3
                                            style={{
                                                textOverflow: "ellipsis",
                                                wordBreak: "break-word",
                                                overflow: "hidden",
                                                minWidth: "0",
                                                maxWidth: "100%",
                                                display: "flex",
                                                padding: "5px 10px",
                                            }}
                                        >
                                            {wp.name}
                                        </H3>
                                    </Grid>
                                    <Grid item xs={3} sm={1}>
                                        <H3
                                            style={{
                                                textAlign: "center",
                                                display: "inline-block",
                                                paddingTop: "5px",
                                                paddingBottom: "5px",
                                                borderRadius: "150px",
                                                color: "white",
                                                backgroundColor: theme.palette.primary.main,
                                                width: "70%",
                                            }}
                                        >
                                            {items.length}
                                        </H3>
                                    </Grid>
                                </Grid>
                            </AccordionSummary>

                            <Grid
                                item
                                container
                                spacing={1}
                                xs={14}
                                columns={14}
                                alignItems={"center"}
                                minHeight={90}
                                paddingBottom={2}
                                paddingLeft={"2vw"}
                            >
                                <Grid xs={2} item>
                                    {t("shift.resource")}
                                </Grid>
                                <Grid xs={2} item>
                                    {t("shift.startTime")}
                                </Grid>
                                <Grid xs={2} item>
                                    {t("shift.endTime")}
                                </Grid>
                                <Grid xs={2} item>
                                    {t("shift.breakStart")}
                                </Grid>
                                <Grid xs={2} item>
                                    {t("shift.breakEnd")}
                                </Grid>
                                <Grid xs={2} item />
                                <Grid xs={2} item />
                            </Grid>

                            {items.map((item, index) => {
                                const id = item.id !== 0 ? item.id : item.localId;
                                const editable = id ? editableMap.get(id) ?? false : false;

                                const assignedEmployee = assignments
                                    .find((a) => a.shiftDetails?.some((detail) => detail.id === item.id))
                                    ?.shiftDetails
                                    ?.find((detail) => detail.id === item.id)
                                    ?.shiftAssignments?.[0]?.employee;

                                return (
                                    <>
                                    <Grid
                                        item
                                        container
                                        spacing={1}
                                        xs={14}
                                        columns={14}
                                        key={item.id}
                                        alignItems={"center"}
                                        minHeight={90}
                                        paddingBottom={2}
                                        sx={{ borderTop: 1, borderColor: "lightgray", paddingLeft: "2vw" }}
                                    >
                                        <Grid xs={2} item>
                                            {assignedEmployee ? (
                                                <Box
                                                    sx={{
                                                        display: "flex",
                                                        alignItems: "center",
                                                        justifyContent: "center",
                                                        padding: "8px 12px",
                                                        borderRadius: "8px",
                                                        fontSize: "0.8rem",

                                                        backgroundColor: "rgba(76, 175, 80, 0.15)", // Light green background for clarity
                                                        color: theme.palette.success.main, // Modern success color for assigned employees
                                                        fontWeight: "bold",
                                                        textAlign: "center",
                                                    }}
                                                >
                                                    {`${assignedEmployee.firstName} ${assignedEmployee.lastName}`}
                                                </Box>
                                            ) : (
                                                <Box
                                                    sx={{
                                                        display: "flex",
                                                        alignItems: "center",
                                                        justifyContent: "center",
                                                        padding: "8px 12px",
                                                        fontSize: "0.8rem",
                                                        borderRadius: "8px",
                                                        backgroundColor: "rgba(244, 67, 54, 0.15)",
                                                        color: theme.palette.error.main,
                                                        fontStyle: "italic",
                                                        textAlign: "center",
                                                    }}
                                                >
                                                    {`${wp.name} #${index + 1}`}
                                                </Box>
                                            )}
                                        </Grid>
                                        <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={it}>
                                            <Grid item xs={2}>
                                                {!editable ? (
                                                    <Box
                                                        sx={{
                                                            padding: "8px 12px",
                                                            borderRadius: "8px",
                                                            border: 3,
                                                            borderColor: "rgba(33, 150, 243, 0.15)",
                                                            color: theme.palette.primary.main,
                                                            fontWeight: "bold",
                                                            textAlign: "center",
                                                            display: "flex",
                                                            alignItems: "center",
                                                            justifyContent: "center",
                                                            minHeight: "40px",
                                                        }}
                                                    >
                                                        {item.startTime
                                                            ? new Date(item.startTime).toLocaleTimeString(undefined, timeOption)
                                                            : ignoreUTC(turnStart, false).toLocaleTimeString(undefined, timeOption)}
                                                    </Box>
                                                ) : (
                                                    <TimePicker
                                                        value={
                                                            item.startTime ? ignoreUTC(new Date(item.startTime), true) : turnStart
                                                        }
                                                        onChange={(value) => {
                                                            if (value && !isNaN(+value)) {
                                                                value.setSeconds(0, 0);
                                                                updateDetail(item.id, {
                                                                    startTime: ignoreUTC(value).toISOString(),
                                                                }, item.localId);
                                                            }
                                                        }}
                                                    />
                                                )}
                                            </Grid>
                                            <Grid item xs={2}>
                                                {!editable ? (
                                                    <Box
                                                        sx={{
                                                            padding: "8px 12px",
                                                            borderRadius: "8px",
                                                            border: 3,
                                                            borderColor: "rgba(33, 150, 243, 0.15)",
                                                            color: theme.palette.primary.main,
                                                            fontWeight: "bold",
                                                            textAlign: "center",
                                                            display: "flex",
                                                            alignItems: "center",
                                                            justifyContent: "center",
                                                            minHeight: "40px",
                                                        }}
                                                    >
                                                        {item.endTime
                                                            ? new Date(item.endTime).toLocaleTimeString(undefined, timeOption)
                                                            : ignoreUTC(turnEnd, false).toLocaleTimeString(undefined, timeOption)}
                                                    </Box>
                                                ) : (
                                                    <TimePicker
                                                        value={
                                                            item.endTime ? ignoreUTC(new Date(item.endTime), true) : turnEnd
                                                        }
                                                        onChange={(value) => {
                                                            if (value && !isNaN(+value)) {
                                                                value.setSeconds(0, 0);
                                                                updateDetail(item.id, {
                                                                    endTime: ignoreUTC(value).toISOString(),
                                                                }, item.localId);
                                                            }
                                                        }}
                                                    />
                                                )}
                                            </Grid>
                                            <Grid item xs={2}>
                                                {!editable ? (
                                                    <Box
                                                        sx={{
                                                            padding: "8px 12px",
                                                            borderRadius: "8px",
                                                            border: 3,
                                                            borderColor: item.breakStart
                                                                ? "rgba(76, 175, 80, 0.15)" // Light green if value exists
                                                                : "rgba(244, 67, 54, 0.15)", // Light red if value is missing
                                                            color: item.breakStart
                                                                ? theme.palette.success.main
                                                                : theme.palette.error.main,
                                                            fontWeight: "bold",
                                                            textAlign: "center",
                                                            display: "flex",
                                                            alignItems: "center",
                                                            justifyContent: "center",
                                                            minHeight: "40px",
                                                        }}
                                                    >
                                                        {item.breakStart
                                                            ? new Date(item.breakStart).toLocaleTimeString(undefined, timeOption)
                                                            : "-"}
                                                    </Box>
                                                ) : (
                                                    <TimePicker
                                                        value={
                                                            item.breakStart ? ignoreUTC(new Date(item.breakStart), true) : null
                                                        }
                                                        onChange={(value) => {
                                                            if (value && !isNaN(+value)) {
                                                                value.setSeconds(0, 0);
                                                                value.setFullYear(1970, 0, 1);
                                                                updateDetail(item.id, {
                                                                    breakStart: ignoreUTC(value).toISOString(),
                                                                }, item.localId);
                                                            } else {
                                                                updateDetail(item.id, { breakStart: undefined }, item.localId);
                                                            }
                                                        }}
                                                        slotProps={{ actionBar: { actions: ["clear"] } }}
                                                    />
                                                )}
                                            </Grid>
                                            <Grid item xs={2}>
                                                {!editable ? (
                                                    <Box
                                                        sx={{
                                                            padding: "8px 12px",
                                                            borderRadius: "8px",
                                                            border: 3,

                                                            borderColor: item.breakEnd
                                                                ? "rgba(76, 175, 80, 0.15)"
                                                                : "rgba(244, 67, 54, 0.15)",
                                                            color: item.breakEnd
                                                                ? theme.palette.success.main
                                                                : theme.palette.error.main,
                                                            fontWeight: "bold",
                                                            textAlign: "center",
                                                            display: "flex",
                                                            alignItems: "center",
                                                            justifyContent: "center",
                                                            minHeight: "40px",
                                                        }}
                                                    >
                                                        {item.breakEnd
                                                            ? new Date(item.breakEnd).toLocaleTimeString(undefined, timeOption)
                                                            : "-"}
                                                    </Box>
                                                ) : (
                                                    <TimePicker
                                                        value={
                                                            item.breakEnd ? ignoreUTC(new Date(item.breakEnd), true) : null
                                                        }
                                                        onChange={(value) => {
                                                            if (value && !isNaN(+value)) {
                                                                value.setSeconds(0, 0);
                                                                value.setFullYear(1970, 0, 1);
                                                                updateDetail(item.id, {
                                                                    breakEnd: ignoreUTC(value).toISOString(),
                                                                }, item.localId);
                                                            } else {
                                                                updateDetail(item.id, { breakEnd: undefined }, item.localId);
                                                            }
                                                        }}
                                                        slotProps={{ actionBar: { actions: ["clear"] } }}
                                                    />
                                                )}
                                            </Grid>
                                        </LocalizationProvider>

                                        <Grid item xs={2}>
                                            {editable ? (
                                                <Button
                                                    onClick={() => {
                                                        const id = item.id !== 0 ? item.id : item.localId;
                                                        if (!id)
                                                            return;
                                                        const newMap = new Map(editableMap);
                                                        newMap.set(id, false);
                                                        setEditableMap(newMap);
                                                    }}
                                                >
                                                    {t("account.confirm.button")}
                                                </Button>
                                            ) : (
                                                <Button
                                                    onClick={() => {
                                                        const id = item.id !== 0 ? item.id : item.localId;
                                                        if (!id)
                                                            return;
                                                        const newMap = new Map(editableMap);
                                                        newMap.set(id, true);
                                                        setEditableMap(newMap);
                                                    }}
                                                >
                                                    {t("common.forms.button.edit")}
                                                </Button>
                                            )}
                                        </Grid>

                                        <Grid item xs={2}>
                                            <IconButton
                                                onClick={() => {
                                                    markDetailDeleted(item.id, item.localId);
                                                }}
                                            >
                                                <DeleteIcon sx={{ alignSelf: "center" }} color="error" />
                                            </IconButton>
                                        </Grid>
                                    </Grid>

                                    {/* Additional Breaks Section */}
                                    {item.additionalBreaks && item.additionalBreaks.length > 0 && (
                                        <Grid
                                            item
                                            container
                                            spacing={1}
                                            xs={14}
                                            columns={14}
                                            sx={{ 
                                                borderTop: 1, 
                                                borderColor: "lightgray", 
                                                paddingLeft: "2vw",
                                                paddingTop: 1,
                                                mt: 0.5,
                                            }}
                                        >
                                            <Grid item xs={14}>
                                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                                                    <Typography
                                                        variant="caption"
                                                        sx={{
                                                            fontWeight: 500,
                                                            color: theme.palette.text.secondary,
                                                            fontSize: '0.75rem',
                                                        }}
                                                    >
                                                        {t("shift.additionalBreaks")} ({item.additionalBreaks.length})
                                                    </Typography>
                                                    {editable && (
                                                        <IconButton
                                                            size="small"
                                                            onClick={() => {
                                                                addAdditionalBreak(item.id, item.localId);
                                                                const id = item.id !== 0 ? item.id : item.localId || '';
                                                                if (!expandedAdditionalBreaks.get(id)) {
                                                                    toggleAdditionalBreaks(id);
                                                                }
                                                            }}
                                                            sx={{
                                                                color: theme.palette.primary.main,
                                                                padding: '4px',
                                                                '&:hover': {
                                                                    backgroundColor: 'rgba(33, 150, 243, 0.08)',
                                                                },
                                                            }}
                                                        >
                                                            <AddIcon fontSize="small" />
                                                        </IconButton>
                                                    )}
                                                </Box>
                                                
                                                <Collapse 
                                                    in={editable ? (expandedAdditionalBreaks.get(item.id !== 0 ? item.id : item.localId || '') ?? true) : true}
                                                    timeout="auto"
                                                    unmountOnExit
                                                >
                                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                                        {item.additionalBreaks.map((additionalBreak, breakIndex) => {
                                                            // For display: use Date directly (timeOption has timeZone: UTC)
                                                            // For TimePicker: use ignoreUTC(..., true) to convert to local time
                                                            const breakStartDateForDisplay = additionalBreak.startTime 
                                                                ? new Date(additionalBreak.startTime) 
                                                                : null;
                                                            const breakEndDateForDisplay = additionalBreak.endTime 
                                                                ? new Date(additionalBreak.endTime) 
                                                                : null;
                                                            const breakStartDateForEdit = additionalBreak.startTime 
                                                                ? ignoreUTC(new Date(additionalBreak.startTime), true) 
                                                                : null;
                                                            const breakEndDateForEdit = additionalBreak.endTime 
                                                                ? ignoreUTC(new Date(additionalBreak.endTime), true) 
                                                                : null;
                                                            
                                                            // Validation checks (only when editing)
                                                            // Only validate if both times are present and non-empty
                                                            const isValid = editable ? (
                                                                additionalBreak.startTime && 
                                                                additionalBreak.endTime && 
                                                                additionalBreak.startTime.trim() !== '' &&
                                                                additionalBreak.endTime.trim() !== '' &&
                                                                isBreakTimeValid(additionalBreak.startTime, additionalBreak.endTime, item) &&
                                                                !doesBreakOverlapWithMain(additionalBreak.startTime, additionalBreak.endTime, item) &&
                                                                !doesBreakOverlapWithOthers(additionalBreak.startTime, additionalBreak.endTime, item, additionalBreak.localBreakId || additionalBreak.id?.toString())
                                                            ) : true;
                                                            
                                                            return (
                                                                <Grid
                                                                    container
                                                                    spacing={1}
                                                                    key={additionalBreak.localBreakId || breakIndex}
                                                                    sx={{
                                                                        alignItems: 'center',
                                                                        p: 1,
                                                                        borderRadius: 1,
                                                                        border: '1px solid',
                                                                        borderColor: isValid 
                                                                            ? 'rgba(0, 0, 0, 0.12)' 
                                                                            : 'rgba(244, 67, 54, 0.5)',
                                                                        backgroundColor: isValid 
                                                                            ? 'transparent'
                                                                            : 'rgba(244, 67, 54, 0.05)',
                                                                    }}
                                                                >
                                                                    {editable ? (
                                                                        <>
                                                                            <Grid item xs={1}>
                                                                                <Chip
                                                                                    label={`#${breakIndex + 1}`}
                                                                                    size="small"
                                                                                    sx={{
                                                                                        backgroundColor: theme.palette.primary.main,
                                                                                        color: 'white',
                                                                                        fontWeight: 500,
                                                                                        height: 24,
                                                                                        fontSize: '0.7rem',
                                                                                    }}
                                                                                />
                                                                            </Grid>
                                                                            <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={it}>
                                                                                <Grid item xs={4}>
                                                                                    <TimePicker
                                                                                        value={breakStartDateForEdit}
                                                                                        onChange={(value) => {
                                                                                            if (value && !isNaN(+value)) {
                                                                                                value.setSeconds(0, 0);
                                                                                                value.setFullYear(1970, 0, 1);
                                                                                                updateAdditionalBreak(
                                                                                                    item.id,
                                                                                                    item.localId,
                                                                                                    additionalBreak.localBreakId || additionalBreak.id?.toString() || '',
                                                                                                    { startTime: ignoreUTC(value).toISOString() }
                                                                                                );
                                                                                            } else {
                                                                                                updateAdditionalBreak(
                                                                                                    item.id,
                                                                                                    item.localId,
                                                                                                    additionalBreak.localBreakId || additionalBreak.id?.toString() || '',
                                                                                                    { startTime: '' }
                                                                                                );
                                                                                            }
                                                                                        }}
                                                                                        slotProps={{ 
                                                                                            actionBar: { actions: ["clear"] },
                                                                                            textField: {
                                                                                                size: 'small',
                                                                                                fullWidth: true,
                                                                                                error: !isValid && breakStartDateForEdit !== null,
                                                                                            }
                                                                                        }}
                                                                                    />
                                                                                </Grid>
                                                                                <Grid item xs={4}>
                                                                                    <TimePicker
                                                                                        value={breakEndDateForEdit}
                                                                                        onChange={(value) => {
                                                                                            if (value && !isNaN(+value)) {
                                                                                                value.setSeconds(0, 0);
                                                                                                value.setFullYear(1970, 0, 1);
                                                                                                updateAdditionalBreak(
                                                                                                    item.id,
                                                                                                    item.localId,
                                                                                                    additionalBreak.localBreakId || additionalBreak.id?.toString() || '',
                                                                                                    { endTime: ignoreUTC(value).toISOString() }
                                                                                                );
                                                                                            } else {
                                                                                                updateAdditionalBreak(
                                                                                                    item.id,
                                                                                                    item.localId,
                                                                                                    additionalBreak.localBreakId || additionalBreak.id?.toString() || '',
                                                                                                    { endTime: '' }
                                                                                                );
                                                                                            }
                                                                                        }}
                                                                                        slotProps={{ 
                                                                                            actionBar: { actions: ["clear"] },
                                                                                            textField: {
                                                                                                size: 'small',
                                                                                                fullWidth: true,
                                                                                                error: !isValid && breakEndDateForEdit !== null,
                                                                                            }
                                                                                        }}
                                                                                    />
                                                                                </Grid>
                                                                            </LocalizationProvider>
                                                                            <Grid item xs={2}>
                                                                                {!isValid && breakStartDateForEdit && breakEndDateForEdit && (
                                                                                    <Typography
                                                                                        variant="caption"
                                                                                        sx={{
                                                                                            color: theme.palette.error.main,
                                                                                            fontSize: '0.7rem',
                                                                                        }}
                                                                                    >
                                                                                        {t("shift.additionalBreakInvalid")}
                                                                                    </Typography>
                                                                                )}
                                                                            </Grid>
                                                                            <Grid item xs={1}>
                                                                                <IconButton
                                                                                    size="small"
                                                                                    onClick={() => removeAdditionalBreak(item.id, item.localId, additionalBreak.localBreakId || additionalBreak.id?.toString() || '')}
                                                                                    sx={{
                                                                                        color: theme.palette.error.main,
                                                                                        padding: '4px',
                                                                                        '&:hover': {
                                                                                            backgroundColor: 'rgba(244, 67, 54, 0.08)',
                                                                                        },
                                                                                    }}
                                                                                >
                                                                                    <DeleteIcon fontSize="small" />
                                                                                </IconButton>
                                                                            </Grid>
                                                                        </>
                                                                    ) : (
                                                                        <>
                                                                            <Grid item xs={1}>
                                                                                <Chip
                                                                                    label={`#${breakIndex + 1}`}
                                                                                    size="small"
                                                                                    sx={{
                                                                                        backgroundColor: theme.palette.primary.main,
                                                                                        color: 'white',
                                                                                        fontWeight: 500,
                                                                                        height: 24,
                                                                                        fontSize: '0.7rem',
                                                                                    }}
                                                                                />
                                                                            </Grid>
                                                                            <Grid item xs={5}>
                                                                                <Box
                                                                                    sx={{
                                                                                        padding: "8px 12px",
                                                                                        borderRadius: "8px",
                                                                                        border: 3,
                                                                                        borderColor: "rgba(33, 150, 243, 0.15)",
                                                                                        color: theme.palette.primary.main,
                                                                                        fontWeight: "bold",
                                                                                        textAlign: "center",
                                                                                        display: "flex",
                                                                                        alignItems: "center",
                                                                                        justifyContent: "center",
                                                                                        minHeight: "40px",
                                                                                    }}
                                                                                >
                                                                                    {breakStartDateForDisplay
                                                                                        ? breakStartDateForDisplay.toLocaleTimeString(undefined, timeOption)
                                                                                        : "-"}
                                                                                </Box>
                                                                            </Grid>
                                                                            <Grid item xs={5}>
                                                                                <Box
                                                                                    sx={{
                                                                                        padding: "8px 12px",
                                                                                        borderRadius: "8px",
                                                                                        border: 3,
                                                                                        borderColor: "rgba(33, 150, 243, 0.15)",
                                                                                        color: theme.palette.primary.main,
                                                                                        fontWeight: "bold",
                                                                                        textAlign: "center",
                                                                                        display: "flex",
                                                                                        alignItems: "center",
                                                                                        justifyContent: "center",
                                                                                        minHeight: "40px",
                                                                                    }}
                                                                                >
                                                                                    {breakEndDateForDisplay
                                                                                        ? breakEndDateForDisplay.toLocaleTimeString(undefined, timeOption)
                                                                                        : "-"}
                                                                                </Box>
                                                                            </Grid>
                                                                            <Grid item xs={3} />
                                                                        </>
                                                                    )}
                                                                </Grid>
                                                            );
                                                        })}
                                                    </Box>
                                                </Collapse>
                                            </Grid>
                                        </Grid>
                                    )}
                                    
                                    {/* Add break button when no breaks exist and editable */}
                                    {editable && (!item.additionalBreaks || item.additionalBreaks.length === 0) && (
                                        <Grid
                                            item
                                            container
                                            spacing={1}
                                            xs={14}
                                            columns={14}
                                            sx={{ 
                                                borderTop: 1, 
                                                borderColor: "lightgray", 
                                                paddingLeft: "2vw",
                                                paddingTop: 1,
                                                mt: 0.5,
                                            }}
                                        >
                                            <Grid item xs={14}>
                                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                                    <Typography
                                                        variant="caption"
                                                        sx={{
                                                            color: theme.palette.text.secondary,
                                                            fontStyle: 'italic',
                                                            fontSize: '0.75rem',
                                                        }}
                                                    >
                                                        {t("shift.noAdditionalBreaks")}
                                                    </Typography>
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => {
                                                            addAdditionalBreak(item.id, item.localId);
                                                            const id = item.id !== 0 ? item.id : item.localId || '';
                                                            if (!expandedAdditionalBreaks.get(id)) {
                                                                toggleAdditionalBreaks(id);
                                                            }
                                                        }}
                                                        sx={{
                                                            color: theme.palette.primary.main,
                                                            padding: '4px',
                                                            '&:hover': {
                                                                backgroundColor: 'rgba(33, 150, 243, 0.08)',
                                                            },
                                                        }}
                                                    >
                                                        <AddIcon fontSize="small" />
                                                    </IconButton>
                                                </Box>
                                            </Grid>
                                        </Grid>
                                    )}
                                    </>
                                );
                            })}
                        </Accordion>
                    );
                })}
        </Box>
    );
};

export default PositionAccordion;
